import { useEffect, useState } from "react";
import { financesApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Kpi,
  Btn,
  DataTable,
  TH,
  TD,
  EmptyState,
} from "@/components/DashboardUI";
import { FileSpreadsheet, TrendingUp, Trophy } from "lucide-react";

export default function Finances() {
  const [ov, setOv] = useState({ revenu: {}, reservations: {}, kpis: {} });
  const [parParc, setParParc] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [o, p, t] = await Promise.all([
          financesApi.overview(),
          financesApi.parParcours(),
          financesApi.topClients(5),
        ]);
        setOv(o);
        setParParc(p);
        setTop(t);
      } catch {
        toast.error("Erreur");
      }
    })();
  }, []);

  async function exportXlsx() {
    try {
      const res = await financesApi.exportXlsx(30);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finances-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel téléchargé");
    } catch {
      toast.error("Erreur export");
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="finances-page">
      <PageHeader
        chapter="Chapitre pro · Le coffre"
        title="Finances"
        subtitle="Revenu, panier moyen, conversion — le pouls comptable."
        testId="fin-header"
        actions={
          <Btn onClick={exportXlsx} data-testid="btn-export-xlsx">
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel (30j)
          </Btn>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi
          value={`${(ov.revenu.aujourdhui || 0).toFixed(2)}€`}
          label="Aujourd'hui"
          accent="or"
          testId="rev-jour"
        />
        <Kpi
          value={`${(ov.revenu.sept_jours || 0).toFixed(2)}€`}
          label="7 derniers j."
          accent="parchemin"
          testId="rev-7j"
        />
        <Kpi
          value={`${(ov.revenu.trente_jours || 0).toFixed(2)}€`}
          label="30 derniers j."
          accent="or"
          testId="rev-30j"
        />
        <Kpi
          value={`${(ov.revenu.mois_en_cours || 0).toFixed(2)}€`}
          label="Mois en cours"
          accent="parchemin"
          testId="rev-mois"
        />
        <Kpi
          value={`${(ov.revenu.total || 0).toFixed(2)}€`}
          label="Revenu total"
          accent="vert"
          testId="rev-total"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* KPIs */}
        <Card>
          <h2 className="flex items-center gap-2 font-display text-lg text-parchemin mb-5">
            <TrendingUp className="w-4 h-4 text-or" strokeWidth={1.5} />
            KPIs
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Stat
              label="Panier moyen"
              value={`${(ov.kpis.panier_moyen || 0).toFixed(2)}€`}
              testid="kpi-panier"
            />
            <Stat
              label="Taux conversion"
              value={`${ov.kpis.taux_conversion || 0}%`}
              testid="kpi-conv"
            />
            <Stat
              label="Résa payées"
              value={ov.reservations.payees || 0}
              testid="kpi-payees"
            />
            <Stat
              label="En attente"
              value={ov.reservations.en_attente || 0}
              testid="kpi-attente"
            />
          </div>
        </Card>

        {/* Top clients */}
        <Card>
          <h2 className="flex items-center gap-2 font-display text-lg text-parchemin mb-5">
            <Trophy className="w-4 h-4 text-or" strokeWidth={1.5} />
            Top 5 clients
          </h2>
          {top.length === 0 ? (
            <EmptyState
              icon={<Trophy className="w-5 h-5" />}
              title="Aucun client encore."
              testId="top-empty"
            />
          ) : (
            <ul className="divide-y divide-or/10">
              {top.map((c, i) => (
                <li
                  key={c.email}
                  className="flex justify-between items-center py-3 first:pt-0 last:pb-0"
                  data-testid={`top-client-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full border border-or/30 bg-noir flex items-center justify-center font-display text-sm text-or">
                      {i + 1}
                    </span>
                    <div>
                      <div className="font-medium text-parchemin">
                        {c.prenom || c.email.split("@")[0]}
                      </div>
                      <div className="text-xs font-mono text-parchemin/50">
                        {c.nb_reservations} résa
                      </div>
                    </div>
                  </div>
                  <span className="font-display text-lg text-or stat-graved">
                    {c.revenu.toFixed(2)}€
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Revenu par parcours */}
      <Card>
        <h2 className="font-display text-lg text-parchemin mb-5">
          Revenu par parcours
        </h2>
        {parParc.length === 0 ? (
          <EmptyState
            icon={<TrendingUp className="w-5 h-5" />}
            title="Aucun parcours encore vendu."
            testId="fin-pp-empty"
          />
        ) : (
          <DataTable testId="fin-parparcours-table">
            <thead>
              <tr>
                <TH>Ville</TH>
                <TH>Parcours</TH>
                <TH align="right">Résa</TH>
                <TH align="right">Payées</TH>
                <TH align="right">Conv.</TH>
                <TH align="right">Revenu</TH>
              </tr>
            </thead>
            <tbody>
              {parParc.map((p) => (
                <tr
                  key={p.parcoursId}
                  className="border-t border-or/10 hover:bg-noir-100/40 transition"
                  data-testid={`fin-pp-${p.parcoursId}`}
                >
                  <TD className="text-parchemin/70">{p.ville}</TD>
                  <TD className="text-parchemin">{p.nom}</TD>
                  <TD align="right">
                    <span className="font-mono">{p.nb_reservations_total}</span>
                  </TD>
                  <TD align="right">
                    <span className="font-mono">{p.nb_reservations_payees}</span>
                  </TD>
                  <TD align="right">
                    <span className="font-mono text-parchemin/70">
                      {p.taux_conversion}%
                    </span>
                  </TD>
                  <TD align="right" className="font-medium font-mono text-or">
                    {p.revenu.toFixed(2)}€
                  </TD>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      className="p-3 rounded-sm bg-noir-100/60 border border-or/10"
      data-testid={testid}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-parchemin/50">
        {label}
      </div>
      <div className="font-display text-xl text-parchemin stat-graved mt-1">
        {value}
      </div>
    </div>
  );
}
